import { useTranslation } from 'react-i18next';

import BlogCards from '@klaviyo-mkt/gatsby-components/organisms/blog-cards';
import Pagination from '@klaviyo-mkt/gatsby-components/molecules/pagination';
import TestimonialsTwoColumn from '@klaviyo-mkt/gatsby-components/organisms/testimonials-two-column';
import { useShouldDisplayBreadcrumbs } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBreadcrumbs';
import NavigationCategorySelection from '@klaviyo-mkt/gatsby-components/organisms/navigation-category-selection';

import useBlogCategoryQuery from '../../../hooks/useBlogCategoryQuery';
import useNewsfeedQuery from '../../../hooks/useNewsfeedQuery';

import { formatCategoryLink } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';

import { ArchiveBlogCard, ArchiveGrid } from '../styles';
import * as s from './styles';

import type { ICategoryArchive } from './types';

const CategoryArchiveLayout = ({ data, pageContext }: ICategoryArchive) => {
  const { t, i18n } = useTranslation();
  const { shouldShowBreadcrumbs, homePrefix } = useShouldDisplayBreadcrumbs('/blog', 'default', true, i18n.language);

  const {
    name,
    description,
    categoryOptions: {
      featuredImage,
      testimonial,
    },
  } = data.wpCategory;

  const {
    numberOfPages,
    pageNumber,
    link,
    previousPagePath,
    nextPagePath,
  } = pageContext;

  const breadcrumbs = [
    {
      link: homePrefix || '/',
      text: t('breadcrumbs_home'),
    },
    {
      link: `${homePrefix}blog`,
      text: t('breadcrumbs_blog'),
    },
    {
      link: formatCategoryLink(t('langCodes_pathPrefix'), link),
      text: name,
    },
  ];

  const flatFeaturedPosts = data.featuredPosts.nodes;
  const blogCategories = useBlogCategoryQuery();
  const newsfeedData = useNewsfeedQuery();

  return (
    <>
      { shouldShowBreadcrumbs && <s.CategoryBreadcrumbs breadcrumbs={breadcrumbs} /> }
      <s.CategoryPage>
        <s.CategoryHero
          categoryArchive
          description={description}
          headline={name}
          image={featuredImage}
          layout='two'
        />

        {(pageNumber === 0) && (
          <>
            {!!flatFeaturedPosts.length && (
              <BlogCards
                backgroundColor="white"
                canShowViewAll={false}
                headline={`${t('templates_archive_category_featured')} ${t('templates_archive_category_articles', { category_name: name.toLowerCase() })}`}
                category={{
                  name,
                  link: link,
                  posts: flatFeaturedPosts,
                }}
                newsfeed={newsfeedData}
              />
            )}

            {testimonial?.quoteText && (
              <TestimonialsTwoColumn
                backgroundColor="light"
                buttons={testimonial.buttons}
                quoteText={testimonial.quoteText}
                {...newsfeedData}
              />
            )}
          </>
        )}

        <ArchiveGrid>
          <s.CategoryCardsTitle
            variant="h3Semibold"
            forwardedAs="h2"
          >
            {t('templates_archive_category_articles').replace('{category_name}', name.toLowerCase())}
          </s.CategoryCardsTitle>

          {data.allWpPost.nodes.map(({ title, uri: postUri, date, excerpt, featuredImage: postFeaturedImage, templatesEditorial }) => (
            <ArchiveBlogCard
              key={JSON.stringify(title + postUri + excerpt)}
              title={templatesEditorial?.postTitle?.shortTitle || title}
              categoryName={name}
              categoryLink={link}
              uri={postUri}
              date={date}
              excerpt={excerpt}
              featuredImage={templatesEditorial?.cardImage || postFeaturedImage?.node}
            />
          ))}

          {(numberOfPages > 1) && (
            <Pagination
              numberOfPages={numberOfPages}
              currentPage={pageNumber}
              baseUrl={formatCategoryLink(t('langCodes_pathPrefix'), link)}
              previousPagePath={previousPagePath}
              nextPagePath={nextPagePath}
            />
          )}
        </ArchiveGrid>

        <NavigationCategorySelection backgroundColor="white" displayAllCategoriesByDefault={true} customPadding={{ customPaddingBottom:'md', customPaddingTop: 'md' }} activeCategory={name} categories={blogCategories || []} />
      </s.CategoryPage>
    </>
  );
};

export default CategoryArchiveLayout;
